// TASK-LOCK: while an agent run works on a task, nobody else moves, re-columns or reassigns it.
// A person can always intervene: "Stop agent and move" (admins, managers, the task's owner: the
// card's `can_stop`) cancels the task's agent runs (`tasks.stopAgent`; `run.cancelled` records
// who stopped them) after a modal confirm; the task is then free to move.
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  toast,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { OctagonXIcon } from "lucide-react";
import type { Api } from "../../../api/client";
import type { CompanyAccess } from "../../company";
import { problemText } from "../../de/de-status";
import type { TaskDetail } from "../view-engine";
import { invalidateWork } from "../work-queries";

export function StopAgentButton({
  api,
  company,
  task,
}: {
  api: Api;
  company: CompanyAccess;
  task: TaskDetail;
}) {
  const qc = useQueryClient();
  const lock = task.agent_lock ?? null;
  const stop = useMutation({
    mutationFn: () => api.client.tasks.stopAgent({ params: { co: company.id, id: task.id } }),
    onSuccess: () => {
      invalidateWork(qc, company.id);
      // Another agent may still hold it: the refreshed task says (its badge stays).
      toast.add({ title: "Agent stopped", type: "success" });
    },
    onError: (e) =>
      toast.add({ title: problemText(e, "The agent was not stopped."), type: "error" }),
  });
  if (!lock) return null;
  const since = new Date(lock.since).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return (
    <>
      <Badge
        variant="neutral"
        data-slot="task-agent-lock"
        title={`${lock.name} is working on this since ${since}`}
      >
        AG working
      </Badge>
      {/* Admins and managers, or the task's owner (the API's `can_stop`). */}
      {lock.can_stop ? (
        <AlertDialog>
          <AlertDialogTrigger render={<Button variant="secondary" size="sm" />}>
            <OctagonXIcon aria-hidden="true" />
            Stop agent and move
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Stop {lock.name}?</AlertDialogTitle>
              <AlertDialogDescription>
                Its run is cancelled and recorded as stopped by you. The task unlocks so you can
                move or reassign it.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep working</AlertDialogCancel>
              <AlertDialogAction variant="destructive" onClick={() => stop.mutate()}>
                Stop agent
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
    </>
  );
}
